import { REACTION } from '~/modules/reactions/types';
import {
  CitationAwareScanResult,
  PlagiarismPdfExport,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/types';
import {
  getCitationAwareScanFailureMessage,
  resolveCitationAwareScanStatus,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/citationAwareScan';
import { parseCitationAwareScanResult } from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/mapScanResult';
import { mapPlagiarismScanFeedbackToReaction } from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/plagiarismScanFeedback';
import {
  PlagiarismV2Export,
  PlagiarismV2ExportSummary,
  PlagiarismV2ScanData,
  PlagiarismV2ScanResult,
  PlagiarismV2ScanSummary,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/plagiarismV2Types';
import {
  ExtendPlagiarismExecutionType,
  PlagiarismCitationAwareReportType,
  PlagiarismExecutionType,
} from '~/modules/tools/views/PlagiarismCheckerV4/types';

const EMPTY_WORDS = {
  freemium_words_total: 0,
  freemium_words_left: 0,
  regular_words_total: 0,
  regular_words_left: 0,
  extra_words_total: 0,
  extra_words_left: 0,
};

export const decodePlagiarismScanTextBase64 = (
  textBase64?: string | null,
): string => {
  if (!textBase64) {
    return '';
  }

  try {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(textBase64, 'base64').toString('utf8');
    }

    const binary = atob(textBase64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
};

const resolveRequestMessage = (
  scan: PlagiarismV2ScanData,
  inputText = '',
): string => {
  return decodePlagiarismScanTextBase64(scan.text_base64) || inputText || '';
};

export const HISTORY_SUMMARY_WORD_COUNT = 5;

export const getHistorySummaryFromText = (
  text: string,
  wordCount = HISTORY_SUMMARY_WORD_COUNT,
): string => {
  if (!text.trim() || wordCount <= 0) {
    return '';
  }

  return text.trim().split(/\s+/).slice(0, wordCount).join(' ');
};

const roundPct = (value: number): number => Math.round(value * 10) / 10;

const getWordsPercentage = (
  words: number | undefined,
  totalWords: number | undefined,
): number | undefined => {
  if (!words || !totalWords) {
    return undefined;
  }

  return (words / totalWords) * 100;
};

export const parseRetryAfterSeconds = (
  headerValue: string | null,
  fallbackSeconds = 3,
): number => {
  if (!headerValue) {
    return fallbackSeconds;
  }

  const parsed = Number.parseInt(headerValue, 10);

  if (Number.isFinite(parsed) && parsed >= 0) {
    return parsed;
  }

  return fallbackSeconds;
};

export const mapPlagiarismPdfExport = (
  exportPayload?: PlagiarismV2ExportSummary | PlagiarismV2Export | null,
): PlagiarismPdfExport | null => {
  if (!exportPayload?.export_id) {
    return null;
  }

  const download = exportPayload.download;

  return {
    export_id: exportPayload.export_id,
    download: download?.url
      ? {
          url: download.url,
          expires_at: download.expires_at,
        }
      : null,
  };
};

export const mapCitationAwareReportFromSummary = (
  summary?: PlagiarismV2ScanSummary | PlagiarismV2ScanResult | null,
): PlagiarismCitationAwareReportType | null => {
  if (!summary) {
    return null;
  }

  const overallSimilarityPct =
    summary.overall_similarity_pct ??
    summary.overall_pct ??
    summary.input_plagiarism_pct;

  if (overallSimilarityPct === undefined) {
    return null;
  }

  const properlyAttributedPct =
    summary.properly_attributed_pct ??
    getWordsPercentage(
      summary.properly_cited_quoted_num_words,
      summary.num_words_total,
    ) ??
    0;
  const needsAttentionPct =
    summary.needs_attention_pct ??
    Math.max(overallSimilarityPct - properlyAttributedPct, 0);

  return {
    overall_similarity_pct: roundPct(overallSimilarityPct),
    needs_attention_pct: roundPct(needsAttentionPct),
    properly_attributed_pct: roundPct(properlyAttributedPct),
    exact_match_pct: roundPct(summary.input_exact_plagiarism_pct || 0),
    minor_match_pct: roundPct(summary.input_minor_plagiarism_pct || 0),
    total_words: summary.num_words_total || 0,
    properly_cited_quoted_words: summary.properly_cited_quoted_num_words || 0,
    omitted_words: summary.omitted_num_words || 0,
    sources_count: summary.sources_count || 0,
    web_sources_count: summary.web_sources_count || 0,
    vector_sources_count: summary.vector_sources_count || 0,
    contains_citations: Boolean(
      summary.contains_citations ??
        (Array.isArray((summary as PlagiarismV2ScanResult).input_citations) &&
          ((summary as PlagiarismV2ScanResult).input_citations as unknown[])
            .length > 0),
    ),
  };
};

export const mapV2PlagiarismScanResponse = (
  scan: PlagiarismV2ScanData,
  inputText = '',
): ExtendPlagiarismExecutionType => {
  const citationScanStatus = resolveCitationAwareScanStatus(scan.status);
  const hasFailedCitationScan = citationScanStatus === 'failed';
  const citationScanResult = hasFailedCitationScan
    ? null
    : parseCitationAwareScanResult(
        (scan.result || {}) as Partial<CitationAwareScanResult> &
          Record<string, unknown>,
      );
  const citationReport = hasFailedCitationScan
    ? null
    : mapCitationAwareReportFromSummary(scan.result || scan.summary);
  const citationScanErrors = citationScanResult?.errors || [];
  const scanId = scan.scan_id || '';
  const hasFeedbackField = scan.feedback !== undefined;
  const requestMessage = resolveRequestMessage(scan, inputText);

  return {
    export_id: scanId,
    scan_id: scanId,
    aggregated_score: citationReport?.overall_similarity_pct || 0,
    identical_words: citationReport
      ? Math.round(
          ((citationReport.exact_match_pct || 0) *
            (citationReport.total_words || 0)) /
            100,
        )
      : 0,
    minor_changed_words: citationReport
      ? Math.round(
          ((citationReport.minor_match_pct || 0) *
            (citationReport.total_words || 0)) /
            100,
        )
      : 0,
    related_meaning_words: 0,
    omitted_words: citationReport?.omitted_words || 0,
    sources_count: citationReport?.sources_count || 0,
    document_link: '',
    reaction: hasFeedbackField
      ? mapPlagiarismScanFeedbackToReaction(scan.feedback)
      : REACTION.NONE,
    request_message: requestMessage,
    is_contain_quotes: citationReport?.contains_citations || false,
    summary: hasFailedCitationScan
      ? getCitationAwareScanFailureMessage(citationScanErrors)
      : getHistorySummaryFromText(requestMessage),
    citation_report: citationReport,
    citation_scan_result: citationScanResult,
    citation_scan_status: citationScanStatus,
    citation_scan_errors: citationScanErrors,
    pdf_export: mapPlagiarismPdfExport(scan.export),
    ai_detector_execution: null,
    ai_model: '',
    created_at: scan.created_at || '',
    updated_at: scan.updated_at || '',
    ...EMPTY_WORDS,
  };
};

export const mapV2PlagiarismListItem = (
  scan: PlagiarismV2ScanData,
): PlagiarismExecutionType => {
  const citationScanStatus = resolveCitationAwareScanStatus(scan.status);
  const hasFailedCitationScan = citationScanStatus === 'failed';
  const citationReport = hasFailedCitationScan
    ? null
    : mapCitationAwareReportFromSummary(scan.summary || scan.result);
  const scanId = scan.scan_id || '';
  const requestMessage = resolveRequestMessage(scan);

  return {
    export_id: scanId,
    scan_id: scanId,
    aggregated_score: citationReport?.overall_similarity_pct || 0,
    identical_words: 0,
    minor_changed_words: 0,
    related_meaning_words: 0,
    omitted_words: citationReport?.omitted_words || 0,
    sources_count: citationReport?.sources_count || 0,
    document_link: '',
    reaction: REACTION.NONE,
    request_message: requestMessage,
    is_contain_quotes: citationReport?.contains_citations || false,
    summary: hasFailedCitationScan
      ? getCitationAwareScanFailureMessage([])
      : getHistorySummaryFromText(requestMessage),
    citation_report: citationReport,
    citation_scan_result: null,
    citation_scan_status: citationScanStatus,
    citation_scan_errors: [],
    pdf_export: mapPlagiarismPdfExport(scan.export),
    ai_detector_execution: null,
    ai_model: '',
    created_at: scan.created_at || '',
    updated_at: scan.updated_at || '',
  };
};
